import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { trackedFunction } from 'reactiveweb/function';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { eq, not } from 'tio-ui/utilities';
import Table from 'tio-reimagined/components/table';
import Select from 'tio-reimagined/components/select';
import Button from 'tio-reimagined/components/button';
import {
  PhCaretLeft,
  PhCaretRight,
  PhWarning,
  PhArrowUp,
  PhArrowDown,
  PhInfo,
} from 'ember-phosphor-icons';
import { OpenSearchQueryBuilder } from 'tio-common/utils/open-search-builder';
import { buildAggregationQuery } from '../../utils/aggregation-query-builder';
import { flattenAggBuckets } from '../../utils/aggregation-response-parser';
import type { ColumnAggregationMap, DateGroupingMap } from '../../utils/aggregation-types';
import { applyColumnFormat, buildUrlValue } from '../../utils/column-format-options';
import { resolveRelativeDateFilters } from '../../utils/relative-date-resolver';
import type {
  ColumnFormatMap,
  UrlFormatConfig,
  AppContext,
} from '../../utils/column-format-options';
import type StoreService from '@ember-data/store';
import type { FieldOption } from '../../utils/opensearch-field-mapper';

interface SearchAdapter {
  search: (query: Record<string, unknown>) => Promise<Record<string, unknown>>;
}

interface OpenSearchHit {
  _source: Record<string, unknown>;
}

type CompanyFilterOperator = 'in' | 'not_in';

const SNAKE_TYPED_SUFFIX_RE = /_(keyword|text|date|integer|float|boolean)$/;
const CAMEL_TYPED_SUFFIX_RE = /(Keyword|Text|Date|Integer|Float|Boolean)$/;

function snakeToCamelKey(key: string): string {
  return key.replace(/_([a-z0-9])/g, (_, ch: string) => ch.toUpperCase());
}

function unique(values: string[]): string[] {
  return [...new Set(values.filter(Boolean))];
}

function segmentCandidates(segment: string, isLeaf: boolean): string[] {
  const camel = snakeToCamelKey(segment);
  const candidates = [segment, camel];

  if (isLeaf) {
    const snakeNoSuffix = segment.replace(SNAKE_TYPED_SUFFIX_RE, '');
    const camelNoSuffix = camel.replace(CAMEL_TYPED_SUFFIX_RE, '');
    candidates.push(snakeNoSuffix, snakeToCamelKey(snakeNoSuffix), camelNoSuffix);
  }

  return unique(candidates);
}

function resolvePathWithVariants(row: Record<string, unknown>, key: string): unknown {
  // If the full key exists as a flat property, use it directly.
  // This handles aggregation rows where dot-containing keys like
  // "employee_ef_fields.employee_email_keyword" are flat properties.
  if (key in row) {
    return row[key];
  }

  const segments = key.split('.').filter(Boolean);
  let current: unknown = row;

  for (let index = 0; index < segments.length; index++) {
    if (current == null || typeof current !== 'object') return undefined;

    // If current is an Array, take the first element
    if (Array.isArray(current)) {
      current = current[0];
      if (current == null || typeof current !== 'object') return undefined;
    }

    const objectCurrent = current as Record<string, unknown>;
    const isLeaf = index === segments.length - 1;
    const segment = segments[index] as string;
    const match = segmentCandidates(segment, isLeaf).find(
      (candidate) => candidate in objectCurrent
    );
    if (!match) return undefined;
    current = objectCurrent[match];
  }

  return current;
}

/**
 * Resolve a cell value from a row object, supporting dot-path keys for
 * nested OpenSearch _source objects (e.g. "employee_ef_fields.employee_email_keyword").
 * When a path segment resolves to an Array, the first element is used.
 */
export function resolveCellValue(row: Record<string, unknown>, key: string): string {
  if (row == null || typeof row !== 'object') return '—';
  let value: unknown = resolvePathWithVariants(row, key);

  if (value === undefined && !key.includes('.')) {
    const fallbackKey = `${key}.value`;
    value = resolvePathWithVariants(row, fallbackKey);
  }

  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) {
    const displayable = value
      .filter((v) => v != null)
      .map((v) => {
        if (typeof v === 'object') {
          const obj = v as Record<string, unknown>;
          // Prefer a human-readable property; fall back to a length-capped
          // JSON serialization so verbose nested objects don't blow out the
          // table cell or leak "[object Object]" to the user.
          const candidate = obj.label ?? obj.name ?? obj.value ?? obj.id;
          if (candidate != null) return String(candidate);
          const json = JSON.stringify(obj);
          return json.length > 80 ? `${json.slice(0, 77)}…` : json;
        }
        return String(v);
      });
    return displayable.length > 0 ? displayable.join(', ') : '—';
  }
  if (value instanceof Date) return value.toLocaleDateString();
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

interface OpenSearchResponse {
  hits?: {
    hits?: OpenSearchHit[];
    total?: { value?: number } | number;
  };
  aggregations?: Record<string, unknown>;
}

export interface PreviewTableSignature {
  Args: {
    columns: FieldOption[];
    allFields?: FieldOption[];
    filterValues: Record<string, unknown>;
    companyId: string | undefined;
    companyIds?: string[];
    companyFilterOperator?: CompanyFilterOperator;
    companyFilterField?: string;
    modelType: string;
    sortField: string | undefined;
    page: number;
    limit: number;
    onSort: (sortField: string) => void;
    onPageChange: (page: number) => void;
    onLimitChange: (limit: number) => void;
    readOnly?: boolean;
    isAggregated?: boolean;
    groupByFieldKeys?: string[];
    columnAggregationMap?: ColumnAggregationMap;
    dateGroupingIntervals?: DateGroupingMap;
    columnFormatMap?: ColumnFormatMap;
    columnLabelMap?: Record<string, string>;
    columnAltTextMap?: Record<string, string>;
    appContext?: AppContext;
  };
}

class PreviewTable extends Component<PreviewTableSignature> {
  @service declare store: StoreService;

  // Dedup key to avoid refiring identical queries
  private lastSearchKey?: string;
  private lastSearchPromise?: Promise<{ hits: Record<string, unknown>[]; total: number }>;

  get hasColumns() {
    return this.args.columns.length > 0;
  }

  get columnKeys() {
    return this.args.columns.map((c) => c.key);
  }

  /** Maps camelCase filter keys to backend-provided snake_case paths */
  get filterKeyToPathMap(): Record<string, string> {
    const map: Record<string, string> = {};
    for (const col of this.args.allFields ?? this.args.columns) {
      map[col.key] = col.path || col.key;
    }
    return map;
  }

  /** Maps filter keys to their FieldOption for queryHints lookup */
  get fieldByKey(): Record<string, FieldOption> {
    const map: Record<string, FieldOption> = {};
    for (const col of this.args.allFields ?? this.args.columns) {
      map[col.key] = col;
    }
    return map;
  }

  // Build and execute the OpenSearch query reactively
  // Matches the same adapter.search() pattern used by programs-data-provider
  searchData = trackedFunction(this, async () => {
    if (!this.hasColumns) return { hits: [], total: 0 };

    const searchAdapter = this.store.adapterFor?.('search') as SearchAdapter | undefined;
    if (!searchAdapter?.search) {
      return { hits: [], total: 0 };
    }

    // Eagerly consume all tracked args so auto-tracking registers them
    // as dependencies even when they're only used in conditional branches.
    const dateGroupingIntervals = this.args.dateGroupingIntervals;

    // Build query with OpenSearchQueryBuilder (same as existing builder)
    const builder = new OpenSearchQueryBuilder();
    // Preview tables render full-cell values from _source; highlighting is unused.
    // Disable default wildcard highlight to keep request payload focused.
    builder.setHighlight([]);

    // Company filter — field name varies per dataset
    const selectedCompanyIds = this.args.companyIds ?? [];
    if (selectedCompanyIds.length > 0) {
      const companyField = this.args.companyFilterField;
      if (!companyField) {
        console.warn('[PreviewTable] companyFilterField not provided; defaulting to "company_id"');
      }
      const filterField = companyField || 'company_id';
      if (this.args.companyFilterOperator === 'not_in') {
        builder.mustNotTerms(filterField, selectedCompanyIds);
      } else {
        builder.mustTerms(filterField, selectedCompanyIds);
      }
    } else if (this.args.companyId) {
      const companyField = this.args.companyFilterField;
      if (!companyField) {
        console.warn('[PreviewTable] companyFilterField not provided; defaulting to "company_id"');
      }
      builder.mustTerm(companyField || 'company_id', this.args.companyId);
    }

    // User-defined filter values — resolve field names to backend-provided paths
    // and use queryHints.filterClause from /fields API to pick the correct clause type.
    // Supports operator suffixes: __gte, __lte, __gt, __lt, __ne, __in, __not_in,
    // __contains, __starts_with, __is_empty, __is_not_empty
    const pathMap = this.filterKeyToPathMap;
    const fieldMap = this.fieldByKey;
    const resolvedFilters = resolveRelativeDateFilters(this.args.filterValues);
    for (const [key, value] of Object.entries(resolvedFilters)) {
      if (value === undefined || value === null || value === '') continue;

      // --- Operator-suffixed filter keys ---
      if (key.endsWith('__is_empty')) {
        const baseKey = key.replace(/__is_empty$/, '');
        const field = pathMap[baseKey] || baseKey;
        builder.mustNotExist(field);
      } else if (key.endsWith('__is_not_empty')) {
        const baseKey = key.replace(/__is_not_empty$/, '');
        const field = pathMap[baseKey] || baseKey;
        builder.exists(field);
      } else if (key.endsWith('__ne')) {
        const baseKey = key.replace(/__ne$/, '');
        const field = pathMap[baseKey] || baseKey;
        builder.mustNotTerm(field, typeof value === 'boolean' ? value : String(value));
      } else if (key.endsWith('__not_in')) {
        const baseKey = key.replace(/__not_in$/, '');
        const field = pathMap[baseKey] || baseKey;
        const values = Array.isArray(value) ? value : [value];
        builder.mustNotTerms(field, values as Array<string | number>);
      } else if (key.endsWith('__in')) {
        const baseKey = key.replace(/__in$/, '');
        const field = pathMap[baseKey] || baseKey;
        const values = Array.isArray(value) ? value : [value];
        builder.mustTerms(field, values as Array<string | number>);
      } else if (key.endsWith('__gt')) {
        const baseKey = key.replace(/__gt$/, '');
        const field = pathMap[baseKey] || baseKey;
        builder.rangeOp(field, 'gt', typeof value === 'number' ? value : String(value));
      } else if (key.endsWith('__gte')) {
        const baseKey = key.replace(/__gte$/, '');
        const field = pathMap[baseKey] || baseKey;
        builder.range(field, String(value), undefined);
      } else if (key.endsWith('__lt')) {
        const baseKey = key.replace(/__lt$/, '');
        const field = pathMap[baseKey] || baseKey;
        builder.rangeOp(field, 'lt', typeof value === 'number' ? value : String(value));
      } else if (key.endsWith('__lte')) {
        const baseKey = key.replace(/__lte$/, '');
        const field = pathMap[baseKey] || baseKey;
        builder.range(field, undefined, String(value));
      } else if (key.endsWith('__contains')) {
        const baseKey = key.replace(/__contains$/, '');
        const field = pathMap[baseKey] || baseKey;
        builder.mustWildcard(field, `*${String(value)}*`);
      } else if (key.endsWith('__starts_with')) {
        const baseKey = key.replace(/__starts_with$/, '');
        const field = pathMap[baseKey] || baseKey;
        builder.mustPrefix(field, String(value));
      } else {
        // No suffix — plain equality or queryHints-based clause
        const field = pathMap[key] || key;
        const clauseType = fieldMap[key]?.queryHints?.filterClause;

        switch (clauseType) {
          case 'match':
            builder.mustMatch(field, String(value));
            break;
          case 'range':
            // Single-value range filter (e.g., exact date); use as gte+lte bound
            builder.range(field, String(value), String(value));
            break;
          case 'terms':
            builder.mustTerms(field, Array.isArray(value) ? value : [value]);
            break;
          case 'term':
          default:
            // term is the safe default; also handles booleans
            if (typeof value === 'boolean') {
              builder.mustBool(field, value);
            } else {
              builder.mustTerm(field, String(value));
            }
            break;
        }
      }
    }

    // Aggregated mode: build agg query with size: 0
    if (this.args.isAggregated && this.groupByKeys.length > 0) {
      const aggQuery = buildAggregationQuery(
        this.groupByKeys,
        this.args.columns,
        this.aggMap,
        this.args.sortField,
        dateGroupingIntervals
      );

      const searchBody = {
        ...builder.build(),
        size: 0,
        aggs: aggQuery,
      };

      const requestPayload = {
        'model-type': this.args.modelType,
        searchBody,
      };

      const requestKey = JSON.stringify(requestPayload);
      if (this.lastSearchKey === requestKey && this.lastSearchPromise) {
        return await this.lastSearchPromise;
      }

      this.lastSearchKey = requestKey;
      this.lastSearchPromise = searchAdapter.search(requestPayload).then((response) => {
        const data = response as OpenSearchResponse;
        const aggregations = data?.aggregations || {};
        const allRows = flattenAggBuckets(aggregations, this.groupByKeys, this.aggMap);
        return { hits: allRows, total: allRows.length };
      });

      try {
        return await this.lastSearchPromise;
      } catch {
        if (this.lastSearchKey === requestKey) {
          this.lastSearchPromise = undefined;
        }
        return { hits: [], total: 0 };
      }
    }

    // Standard hits mode: pagination, sort, column projection
    builder.setPagination(this.args.page, this.args.limit);

    // Sort
    if (this.args.sortField) {
      builder.setSort(this.args.sortField);
    } else {
      builder.setSort('_score');
    }

    // Return only the fields needed by the visible report columns.
    // Include both canonical path and key fallback so legacy column configs
    // still resolve even when one representation is unavailable.
    const selectedSourcePaths = this.args.columns
      .flatMap((col) => [col.path, col.key])
      .filter((value): value is string => typeof value === 'string' && value.trim().length > 0);
    builder.selectSourceFields(selectedSourcePaths);

    const searchBody = builder.build();

    // Build the request payload matching the adapter's expected format
    const requestPayload = {
      'model-type': this.args.modelType,
      searchBody,
    };

    // Request dedup (same pattern as programs-data-provider)
    const requestKey = JSON.stringify(requestPayload);
    if (this.lastSearchKey === requestKey && this.lastSearchPromise) {
      return await this.lastSearchPromise;
    }

    this.lastSearchKey = requestKey;
    this.lastSearchPromise = searchAdapter.search(requestPayload).then((response) => {
      const data = response as OpenSearchResponse;
      const hits = (data?.hits?.hits || []).map((hit: OpenSearchHit) => hit._source);
      const rawTotal = data?.hits?.total;
      const total = typeof rawTotal === 'number' ? rawTotal : (rawTotal?.value ?? 0);
      return { hits, total };
    });

    try {
      return await this.lastSearchPromise;
    } catch {
      if (this.lastSearchKey === requestKey) {
        this.lastSearchPromise = undefined;
      }
      return { hits: [], total: 0 };
    }
  });

  get groupByKeys() {
    return this.args.groupByFieldKeys || [];
  }

  get aggMap() {
    return this.args.columnAggregationMap || {};
  }

  get rows() {
    const allRows = this.searchData.value?.hits || [];
    // Client-side pagination for aggregated mode
    if (this.args.isAggregated) {
      const start = (this.args.page - 1) * this.args.limit;
      return allRows.slice(start, start + this.args.limit);
    }
    return allRows;
  }

  get total() {
    return this.searchData.value?.total || 0;
  }

  get isLoading() {
    return this.searchData.isLoading;
  }

  get totalPages() {
    return Math.max(1, Math.ceil(this.total / this.args.limit));
  }

  get canGoBack() {
    return this.args.page > 1;
  }

  get canGoForward() {
    return this.args.page < this.effectiveTotalPages;
  }

  /** OpenSearch offset pagination is limited to 10,000 results (from + size) */
  get isNearOffsetLimit() {
    // Aggregated mode uses size: 0 with client-side pagination — no offset limit applies
    if (this.args.isAggregated) return false;
    return this.total > 10_000;
  }

  get offsetLimitWarning() {
    if (!this.isNearOffsetLimit) return '';
    return `Showing first 10,000 of ${this.total.toLocaleString()} results. Use Export CSV to download all data.`;
  }

  /** Clamp totalPages so we never request beyond the 10K offset window */
  get effectiveTotalPages() {
    if (this.isNearOffsetLimit) {
      return Math.min(this.totalPages, Math.floor(10_000 / this.args.limit));
    }
    return this.totalPages;
  }

  getCellValue = (row: Record<string, unknown>, col: FieldOption) => {
    // Resolve the raw value for potential formatting
    const formatConfig = this.args.columnFormatMap?.[col.key];
    if (formatConfig) {
      // URL format rendering is handled separately in getUrlLink
      if (formatConfig.type === 'url') {
        const rawKey = col.resultKey || col.key;
        let raw = resolvePathWithVariants(row, rawKey);
        if (raw === undefined && col.resultKey) {
          raw = resolvePathWithVariants(row, col.key);
        }
        if (raw === null || raw === undefined || raw === '') return '—';
        return String(raw);
      }

      const rawKey = col.resultKey || col.key;
      let raw = resolvePathWithVariants(row, rawKey);
      if (raw === undefined && col.resultKey) {
        raw = resolvePathWithVariants(row, col.key);
      }
      if (raw != null) {
        const formatted = applyColumnFormat(raw, formatConfig);
        if (formatted != null) return formatted;
      }
    }

    // Fallback: standard cell resolution
    if (col.resultKey) {
      const value = resolveCellValue(row, col.resultKey);
      if (value !== '—') return value;
    }
    return resolveCellValue(row, col.key);
  };

  /**
   * Get URL link data for a cell if the column has URL formatting.
   * Returns { href, label } or null.
   */
  getUrlLink = (row: Record<string, unknown>, col: FieldOption) => {
    const formatConfig = this.args.columnFormatMap?.[col.key];
    if (!formatConfig || formatConfig.type !== 'url') return null;
    const rawKey = col.resultKey || col.key;
    let raw = resolvePathWithVariants(row, rawKey);
    if (raw === undefined && col.resultKey) {
      raw = resolvePathWithVariants(row, col.key);
    }
    return buildUrlValue(raw, formatConfig as UrlFormatConfig, this.args.appContext);
  };

  isUrlColumn = (col: FieldOption) => {
    return this.args.columnFormatMap?.[col.key]?.type === 'url';
  };

  handleSort = (field: string) => {
    const current = this.args.sortField;
    // Strip existing direction to get the base field
    const currentBase = current?.replace(/:(?:asc|desc)$/, '');
    const isCurrentDesc = current?.endsWith(':desc');

    if (currentBase === field && !isCurrentDesc) {
      // Currently asc → toggle to desc
      this.args.onSort(`${field}:desc`);
    } else {
      // Not sorted or currently desc → sort asc
      this.args.onSort(`${field}:asc`);
    }
  };

  getSortDirection = (sortPath: string) => {
    const current = this.args.sortField;
    if (!current) return '';
    const currentBase = current.replace(/:(?:asc|desc)$/, '');
    if (currentBase !== sortPath) return '';
    return current.endsWith(':desc') ? 'desc' : 'asc';
  };

  getColumnLabel = (key: string) => {
    return this.args.columnLabelMap?.[key] || '';
  };

  getColumnAltText = (key: string) => {
    return this.args.columnAltTextMap?.[key] || '';
  };

  @tracked altTextVisibleKey: string | null = null;
  private _altTextCleanup: (() => void) | null = null;

  toggleAltText = (key: string) => {
    if (this.altTextVisibleKey === key) {
      this.dismissAltText();
    } else {
      this.altTextVisibleKey = key;
      // Defer so this click doesn't immediately dismiss
      requestAnimationFrame(() => {
        // Guard: if popover was closed before rAF ran, bail out
        if (this.altTextVisibleKey !== key) return;
        const handler = (e: MouseEvent) => {
          const target = e.target as HTMLElement;
          if (!target.closest('[data-alt-text-popover]')) {
            this.dismissAltText();
          }
        };
        document.addEventListener('click', handler, { once: true });
        this._altTextCleanup = () => document.removeEventListener('click', handler);
      });
    }
  };

  private dismissAltText = () => {
    this.altTextVisibleKey = null;
    this._altTextCleanup?.();
    this._altTextCleanup = null;
  };

  isAltTextVisible = (key: string) => {
    return this.altTextVisibleKey === key;
  };

  goToPrevPage = () => {
    if (this.canGoBack) {
      this.args.onPageChange(this.args.page - 1);
    }
  };

  goToNextPage = () => {
    if (this.canGoForward) {
      this.args.onPageChange(this.args.page + 1);
    }
  };

  handleLimitChange = (key: string) => {
    this.args.onLimitChange(Number(key));
  };

  get limitOptions() {
    return [
      { key: '10', label: '10 per page' },
      { key: '25', label: '25 per page' },
      { key: '50', label: '50 per page' },
      { key: '100', label: '100 per page' },
    ];
  }

  get selectedLimitKey() {
    return String(this.args.limit);
  }

  <template>
    <div class="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
      <div class="flex items-center gap-2 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <span class="text-sm font-semibold text-gray-600 dark:text-gray-300">📋 Report Preview</span>
      </div>

      {{#if this.hasColumns}}
        <div class="overflow-x-auto">
          <Table @isLoading={{this.isLoading}} @stickyHeader={{true}}>
            <:thead as |t|>
              <t.tr>
                {{#each @columns as |col|}}
                  <t.th>
                    <div class="group flex items-center gap-1">
                      <span>{{if
                          (this.getColumnLabel col.key)
                          (this.getColumnLabel col.key)
                          col.label
                        }}</span>
                      {{#if (this.getColumnAltText col.key)}}
                        <span class="relative">
                          <button
                            type="button"
                            aria-label="Column info"
                            class="inline-flex text-blue-400 hover:text-blue-600 dark:text-blue-500 dark:hover:text-blue-300"
                            {{on "click" (fn this.toggleAltText col.key)}}
                          >
                            <PhInfo @size={{14}} />
                          </button>
                          {{#if (this.isAltTextVisible col.key)}}
                            <div
                              data-alt-text-popover
                              class="absolute left-0 top-full z-50 mt-1 w-56 rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 p-2 text-xs text-gray-600 dark:text-gray-300 shadow-lg"
                            >
                              {{this.getColumnAltText col.key}}
                            </div>
                          {{/if}}
                        </span>
                      {{/if}}
                      {{#if col.sortPath}}
                        {{#if (eq (this.getSortDirection col.sortPath) "asc")}}
                          <button
                            type="button"
                            aria-label="Sort {{col.label}} descending"
                            aria-pressed="true"
                            class="inline-flex items-center justify-center rounded-md bg-emerald-50 p-1 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:ring-emerald-700/60"
                            {{on "click" (fn this.handleSort col.sortPath)}}
                          >
                            <PhArrowUp @size={{18}} @weight="bold" />
                          </button>
                        {{else if (eq (this.getSortDirection col.sortPath) "desc")}}
                          <button
                            type="button"
                            aria-label="Sort {{col.label}} ascending"
                            aria-pressed="true"
                            class="inline-flex items-center justify-center rounded-md bg-emerald-50 p-1 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:ring-emerald-700/60"
                            {{on "click" (fn this.handleSort col.sortPath)}}
                          >
                            <PhArrowDown @size={{18}} @weight="bold" />
                          </button>
                        {{else}}
                          <button
                            type="button"
                            aria-label="Sort {{col.label}} ascending"
                            class="p-0.5 text-gray-400 dark:text-gray-500 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                            {{on "click" (fn this.handleSort col.sortPath)}}
                          >
                            <PhArrowUp @size={{14}} />
                          </button>
                        {{/if}}
                      {{/if}}
                    </div>
                  </t.th>
                {{/each}}
              </t.tr>
            </:thead>
            <:tbody as |t|>
              {{#each this.rows as |row|}}
                <t.tr>
                  {{#each @columns as |col|}}
                    <t.td>
                      {{#let (this.getUrlLink row col) as |urlLink|}}
                        {{#if urlLink}}
                          <a
                            href={{urlLink.href}}
                            class="text-indigo-600 dark:text-indigo-400 underline hover:text-indigo-800 dark:hover:text-indigo-300"
                            target="_blank"
                            rel="noopener noreferrer"
                          >{{urlLink.label}}</a>
                        {{else}}
                          {{this.getCellValue row col}}
                        {{/if}}
                      {{/let}}
                    </t.td>
                  {{/each}}
                </t.tr>
              {{/each}}
              {{#if (eq this.rows.length 0)}}
                <t.tr>
                  <t.td>
                    <div class="text-center text-gray-400 dark:text-gray-500 py-8 col-span-full">
                      No data found
                    </div>
                  </t.td>
                </t.tr>
              {{/if}}
            </:tbody>
          </Table>
        </div>

        {{! Pagination }}
        <div
          class="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700"
        >
          <div class="flex items-center gap-2">
            <span class="text-sm text-gray-500 dark:text-gray-400">
              {{this.total}}
              results
            </span>
            {{#if this.isNearOffsetLimit}}
              <span
                class="inline-flex items-center gap-1 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 rounded-full px-2 py-0.5"
              >
                <PhWarning @size={{12}} />
                {{this.offsetLimitWarning}}
              </span>
            {{/if}}
          </div>
          <div class="flex items-center gap-3">
            <Select
              @items={{this.limitOptions}}
              @selected={{this.selectedLimitKey}}
              @onChange={{this.handleLimitChange}}
              @size="sm"
              @clearable={{false}}
            />
            <div class="flex items-center gap-1">
              <Button
                @appearance="outlined"
                @size="sm"
                @icon={{PhCaretLeft}}
                @isDisabled={{not this.canGoBack}}
                {{on "click" this.goToPrevPage}}
              />
              <span class="text-sm text-gray-600 dark:text-gray-300 px-2">
                {{@page}}
                /
                {{this.effectiveTotalPages}}
              </span>
              <Button
                @appearance="outlined"
                @size="sm"
                @icon={{PhCaretRight}}
                @isDisabled={{not this.canGoForward}}
                {{on "click" this.goToNextPage}}
              />
            </div>
          </div>
        </div>
      {{else}}
        <div class="flex items-center justify-center h-48 text-gray-400 dark:text-gray-500 text-sm">
          No columns selected. Add fields to build your report.
        </div>
      {{/if}}
    </div>
  </template>
}

export default PreviewTable;
